import { useEffect, useState } from 'react';

// Audience-builder (drawn boundaries) — AU postcode polygons, LOAD-ON-DEMAND.
//
// HOSTING DECISION (per the investigation): a PRE-SPLIT, statically-hosted dataset —
// one GeoJSON file per postcode at `${VITE_AU_POSTCODE_GEOJSON_BASE_URL}/${postcode}.json`
// (derived from ABS POA boundaries, simplified). The client fetches ONLY the handful of
// postcodes actually targeted (you target a few per ad set, not all ~2,600), and caches
// each. No server endpoint, no bundled dataset, CDN-cacheable.
//
// OPERATOR STEP: host the per-postcode GeoJSON (e.g. a Cloud Storage bucket with CORS
// allowed) and set VITE_AU_POSTCODE_GEOJSON_BASE_URL. When unset, postcodes simply stay
// chips (not drawn) — graceful, same as a geocode miss for points.

const BASE_URL = (import.meta.env.VITE_AU_POSTCODE_GEOJSON_BASE_URL as string | undefined) ?? '';

// Module cache (incl. cached misses) so a postcode is fetched at most once per session.
const cache = new Map<string, unknown | null>();

// Extract the 4-digit AU postcode from a Meta zip result's name/key (e.g. "4217" or
// "4217, Queensland"). Returns null when there's no 4-digit code (e.g. a NZ/other zip),
// so non-AU postcodes are simply not drawn. Pure.
export function normalizeAuPostcode(nameOrKey: string): string | null {
  const m = /\b(\d{4})\b/.exec(String(nameOrKey ?? ''));
  return m ? m[1]! : null;
}

// Per-postcode GeoJSON URL (pure).
export function auPostcodeUrl(base: string, postcode: string): string {
  return `${base.replace(/\/+$/, '')}/${postcode}.json`;
}

// Fetch one postcode's polygon. Returns null when unconfigured / not-found / on error
// (graceful — the caller leaves it as a chip). `opts` injectable for tests.
export async function fetchAuPostcodePolygon(
  postcode: string,
  opts: { base?: string; fetcher?: typeof fetch } = {},
): Promise<unknown | null> {
  const base = opts.base ?? BASE_URL;
  if (!base) return null;
  if (cache.has(postcode)) return cache.get(postcode)!;
  const fetcher = opts.fetcher ?? fetch;
  let result: unknown | null = null;
  try {
    const res = await fetcher(auPostcodeUrl(base, postcode));
    if (res.ok) result = await res.json();
  } catch {
    result = null;
  }
  cache.set(postcode, result);
  return result;
}

// Test-only cache reset.
export function resetAuPostcodeCache(): void {
  cache.clear();
}

// Resolve polygons for a set of zips on demand. Keyed by the zip's `name` (the postcode
// string) to match geoBoundaries' lookup. Re-runs when the set of postcodes changes;
// the module cache makes repeats instant. Only AU (4-digit) postcodes are fetched.
export function useAuPostcodePolygons(zips: { name: string }[]): Record<string, unknown> {
  const keyList = zips.map((z) => z.name).join('|');
  const [polys, setPolys] = useState<Record<string, unknown>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const out: Record<string, unknown> = {};
      for (const z of zips) {
        const pc = normalizeAuPostcode(z.name);
        if (!pc) continue; // non-AU / no 4-digit code → not drawn
        const poly = await fetchAuPostcodePolygon(pc);
        if (poly) out[z.name] = poly;
      }
      if (!cancelled) setPolys(out);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyList captures `zips`
  }, [keyList]);

  return polys;
}
